import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Node, WorkflowOutput } from "../types/workflow";
import customFetch from "../utils/customFetch";
import { NodeConfig } from "../types/nodes";

export type SaveActivityNodeProps = {
  name: string;
  workflow: WorkflowOutput;
  action: string;
  service: string;
  config?: string;
}

export type ActivityInfo = {
  name: string;
  capabilities: string[];
  metadata: Record<string, any>;
};

export type AgentTool = {
  id: string;
  name: string;
  service: string;
  action: string;
  config: Record<string, any>;
  service_auth_token_id?: string;
};

export const useGetActivityNodesByWorkflowId = () => {
    return useMutation<Node[], ErrorEvent, { workflowId: string }>({
        mutationFn: ({ workflowId }) => {
          return customFetch(`/v1/activity-nodes?workflow=${workflowId}`)
            .then(async(res) => {
              const data = await res.json();
              return res.ok
                ? data
                : Promise.reject(new Error(`Failed to fetch activity nodes for workflow ID ${workflowId}`))
            })
            .catch((err) => toast.error(err.message));
        }
      });
}

export const useGetActivityNodeByNodeId = () => {
  return useMutation<Node, ErrorEvent, { nodeId: string }>({
      mutationFn: ({ nodeId }) => {
        return customFetch(`/v1/activity-nodes/${nodeId}`)
          .then(async(res) => {
            const data = await res.json();
            return res.ok
              ? data
              : Promise.reject(new Error(`Failed to fetch activity node with ID ${nodeId}`))
          })
          .catch((err) => toast.error(err.message));
      }
    });
}

export const useDeleteActivityNodeById = () => {
  return useMutation<null, ErrorEvent, { nodeId: string }>({
    mutationFn: ({ nodeId }) => {
      return customFetch(`/v1/activity-nodes/${nodeId}`, {
        method: "DELETE"
      })
      .then(async(res) => {
        const data = await res.json();
        if(res.ok) {
          toast.success("Activity Node deleted successfully");
          return data;
        }
        return Promise.reject(new Error(`Failed to delete activity node`))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useSaveActivityNode = () => {
  return useMutation<Node, ErrorEvent, SaveActivityNodeProps>({
    mutationFn: (nodeProps) => {
      return customFetch(`/v1/activity-nodes`, {
        method: 'POST',
        body: JSON.stringify(nodeProps)
      })
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to save activity node'))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useSaveActivityNodeWithPatch = () => {
  return useMutation<Node, ErrorEvent, Node>({
    mutationFn: (nodeProps) => {
      nodeProps.save_state = 0;
      return customFetch(`/v1/activity-nodes/${nodeProps.id}`, {
        method: 'PATCH',
        body: JSON.stringify(nodeProps)
      })
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to save activity node'))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useGetConfigsForActivityNode = () => {
  const queryClient = useQueryClient();
  return useMutation<any, ErrorEvent, string>({
    mutationFn: (nodeId) => {
      return customFetch(`/v1/activity-nodes/getConfigs/${nodeId}`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to fetch configs for activity node`))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityNodes'] });
    },
  });
};

export const useGetAvailableActivities = () => {
  return useQuery<ActivityInfo[], ErrorEvent>({
    queryKey: ['availableActivities'],
    queryFn: () => {
      return customFetch(`/v1/activities`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to fetch available activities`))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    },
  });
};

export const useUpdateActivityNodeName = () => {
  return useMutation<Node, ErrorEvent, { nodeId: string, name: string }>({
    mutationFn: ({ nodeId, name }) => {
      return customFetch(`/v1/activity-nodes/${nodeId}/name`, {
        method: 'PATCH',
        body: JSON.stringify({ name })
      })
      .then(async(res) => {
        const data = await res.json();
        if(res.ok) {
          toast.success("Activity Node name updated successfully");
          return data;
        }
        return Promise.reject(new Error(`Failed to update activity node name`))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useGetToolsForActivityNode = (nodeId?: string) => {
  return useQuery<AgentTool[], ErrorEvent>({
    queryKey: ['activityNodeTools', nodeId],
    enabled: !!nodeId, // Only run the query if nodeId is provided
    queryFn: () => {
      return customFetch(`/v1/activity-nodes/${nodeId}/tools`)
        .then(async(res) => {
          const data = await res.json();
          // Convert ActivityNode entities to AgentTool type for frontend use
          const tools = data.map((node: any) => {
            let config = {};
            try {
              // Try to parse the config JSON string
              if (node.config) {
                config = JSON.parse(node.config);
              }
            } catch (e) {
              console.error('Failed to parse tool config:', e);
            }
            
            return {
              id: node.id || '',
              name: node.name || '',
              service: node.service || '',
              action: node.action || '',
              config: config,
              service_auth_token_id: node.service_auth_token_id || undefined
            };
          });
          return res.ok
            ? tools
            : Promise.reject(new Error(`Failed to fetch tools for activity node`))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    }
  });
};

export const useSaveToolForActivityNode = () => {
  const queryClient = useQueryClient();
  return useMutation<any, ErrorEvent, { nodeId: string, tool: Partial<AgentTool> }>({
    mutationFn: ({ nodeId, tool }) => {
      // Convert AgentTool to a format expected by the backend
      const toolData = {
        id: tool.id,
        name: tool.name,
        service: tool.service,
        action: tool.action,
        config: tool.config
      };
      
      return customFetch(`/v1/activity-nodes/${nodeId}/tools`, {
        method: 'POST',
        body: JSON.stringify(toolData)
      })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to save tool for activity node`))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific query for this node's tools
      queryClient.invalidateQueries({ queryKey: ['activityNodeTools', variables.nodeId] });
    }
  });
};

export const useDeleteToolFromActivityNode = () => {
  const queryClient = useQueryClient();
  return useMutation<any, ErrorEvent, { nodeId: string, toolId: string }>({
    mutationFn: ({ nodeId, toolId }) => {
      return customFetch(`/v1/activity-nodes/${nodeId}/tools/${toolId}`, {
        method: 'DELETE'
      })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to delete tool from activity node`))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific query for this node's tools
      queryClient.invalidateQueries({ queryKey: ['activityNodeTools', variables.nodeId] });
    }
  });
};

export const useUpdateActivityNodeAuthToken = () => {
  const queryClient = useQueryClient();
  return useMutation<Node, ErrorEvent, { nodeId: string, serviceAuthTokenId: string }>({
    mutationFn: ({ nodeId, serviceAuthTokenId }) => {
      return customFetch(`/v1/activity-nodes/${nodeId}/auth-token`, {
        method: 'PATCH',
        body: JSON.stringify({ service_auth_token_id: serviceAuthTokenId })
      })
        .then(async(res) => {
          const data = await res.json();
          if(res.ok) {
            toast.success("Auth token updated successfully");
            return data;
          }
          return Promise.reject(new Error(`Failed to update auth token`))
        })
        .catch((err) => {
          toast.error(err.message);
          throw err;
        });
    },
    onSuccess: (_, variables) => {
      // Invalidate queries that might be affected by this update
      queryClient.invalidateQueries({ queryKey: ['activityNodeTools', variables.nodeId] });
      queryClient.invalidateQueries({ queryKey: ['activityNodes'] });
    }
  });
};
